import React, { useCallback, useEffect } from "react";
import { useAddressSearchPreview } from "./hooks/useAddressSearchPreview";
import { useCountyAutocomplete } from "./hooks/useCountyAutocomplete";
import { useSearchHandlers } from "./hooks/useSearchHandlers";
import { useTabState } from "./hooks/useTabState";
import { fetchCityStateForZip } from "./utils/apiUtils";
import AddressTab from "./components/AddressTab";
import OwnerTab from "./components/OwnerTab";
import ParcelTab from "./components/ParcelTab";
import PropertyPreview from "./components/PropertyPreview";

/**
 * AddressSearchPreview Component
 * =============================
 *
 * Main property search interface with tabbed navigation.
 * Orchestrates address search, parcel search, and property enrichment.
 *
 * ARCHITECTURE OVERVIEW:
 * =====================
 *
 * ┌─────────────────┐    ┌─────────────────┐    ┌─────────────────┐
 * │   Tab State     │────│ Address Search   │────│  Property       │
 * │  (useTabState)  │    │  Enrichment      │    │  Preview       │
 * └─────────────────┘    └─────────────────┘    └─────────────────┘
 *         │                        │                     │
 *         ▼                        ▼                     ▼
 * ┌─────────────────┐    ┌─────────────────┐    ┌─────────────────┐
 * │   Address Tab   │    │   Parcel Tab    │    │   Owner Tab     │
 * │ (Autocomplete)  │    │ (Parcel Search) │    │ (Owner Search)  │
 * └─────────────────┘    └─────────────────┘    └─────────────────┘
 *
 * FLOW SUMMARY:
 * ============
 * 1. User selects tab (Address/Owner/Parcel)
 * 2. User searches using selected method
 * 3. Results populate form fields (with zip validation)
 * 4. Property enrichment fetches additional data
 * 5. PropertyPreview shows results or error states
 *
 * KEY FEATURES:
 * - Unified state management with useTabState hook
 * - Zip validation prevents unsupported addresses
 * - Clean separation between search methods
 * - Comprehensive error handling and user feedback
 *
 * @param {string} mode - Calculator mode (seller/buyer/etc)
 * @param {Object} control - React Hook Form control
 * @param {Function} setValue - React Hook Form setValue
 * @param {Function} getValues - React Hook Form getValues
 * @param {Function} showToaster - Toast notification function
 */
const AddressSearchPreview = React.memo(({
  mode = "seller",
  initialHeader = "",
  onEnriched = () => { },
  onAddressEdited = () => { },
  onAddressSelected = () => { },
  onStreetViewChanged = () => { },
  className = "",
  imageWidth = 431,
  imageHeight = 232,
  control,
  setValue = undefined,
  getValues,
  openModal,
  closeModal,
  showToaster,
}) => {

  // ============================================================================
  // CUSTOM HOOKS - Clean separation of concerns
  // ============================================================================

  // Tab and selection state management
  const tabState = useTabState();

  // Handle enrichment completion - update form fields with API data including closing_county
  const handleEnriched = useCallback((enrichmentData) => {
 //   console.log('🎉 [AddressSearchPreview] Enrichment completed:', enrichmentData);

    if (enrichmentData?.payload) {
      const payload = enrichmentData.payload;
  //    console.log('📝 [AddressSearchPreview] Updating address data with enrichment results');

      // Update tabState with enriched data including closing_county
      tabState.setAddressData({
        street_no: payload.street_no || "",
        street_name: payload.route || "",
        city: payload.city || "",
        state: payload.state_code || payload.state || "",
        closing_state: payload.state_code || payload.state || "", // Also set closing_state for SetCountyModal
        zip: payload.zip || "",
        closing_county: payload.closing_county || "",
        unitNumber: payload.site_unit || "",
        formattedAddress: payload.formatted_address || "",
      });

    //  console.log('✅ [AddressSearchPreview] Address data updated with closing_county:', payload.closing_county);
    }

    // Call the original onEnriched callback if provided
    onEnriched(enrichmentData);
  }, [onEnriched, tabState]);


  // ============================================================================
  // ADDRESS SELECTION HANDLERS
  // ============================================================================

  // Handle address selection from autocomplete (Google Places)
  const handleAddressSelected = useCallback((addressData) => {
    tabState.setAddressData(addressData);
  }, [tabState]);

  // Handle zip validation failure
  const handleZipValidationFailure = useCallback(() => {
    tabState.setZipValidationError();
  }, [tabState]);

  // Scroll to AddressTab when it becomes active
  useEffect(() => {
    if (tabState.activeTab === "Address" && tabState.addressTabRef.current) {
      tabState.addressTabRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [tabState.activeTab, tabState.addressTabRef]);

  // County autocomplete hooks for Owner and Parcel tabs
  const ownerCountyAutocomplete = useCountyAutocomplete();
  const parcelCountyAutocomplete = useCountyAutocomplete();

  // Address search hook (called first so we can pass applyManualAddressEnrichment to useSearchHandlers)
  const {
    query,
    header,
    suggestions,
    activeIndex,
    isLoadingEnrichment,
    showPreview,
    propertyNotFound,
    ttbData,
    showSiteUnit,
    siteUnit,
    clearPropertyNotFound,
    applyManualAddressEnrichment,

    onChange,
    onBlur,
    onKeyDown,
    selectSuggestion,
    setActiveIndex,
    handleSiteUnitCheckboxChange,
    handleSiteUnitInputChange,
    patch, // For programmatic updates without triggering autocomplete
    suppressPredictions, // For suppressing autocomplete

    suggestionItemRefs,
    panoramaContainerRef,
    satelliteContainerRef,
  } = useAddressSearchPreview({
    mode,
    initialHeader,
    onEnriched: handleEnriched,
    onAddressEdited,
    onAddressSelected: handleAddressSelected,
    onStreetViewChanged,
  });

  // Callback for manual address search result (get-city-state-for-zip) – same behaviour as autocomplete
  const handleManualAddressResult = useCallback(
    (err, enrichmentResult, requestPayload) => {
    //  console.log("handleManualAddressResult", { err, enrichmentResult, requestPayload });
   //   console.log("Request payload site_unit:", requestPayload?.site_unit);
      if (err || !enrichmentResult) {
        // Handle failure case - show property not found
        applyManualAddressEnrichment({ success: false }, requestPayload || {});
      } else {
        // Handle success case - use enrichment result object
        applyManualAddressEnrichment(enrichmentResult, requestPayload || {});
    //    console.log("enrichmentResult", enrichmentResult);
        // For manual search, update address fields with any corrected data from API
        if (enrichmentResult.success && enrichmentResult.payload) {
          const fullAddress = requestPayload.street_no && requestPayload.route_long ?
            `${requestPayload.street_no} ${requestPayload.route_long}, ${requestPayload.city}, ${requestPayload.state_code} ${requestPayload.zip}` : "";
          const hasUnit = !!(requestPayload.site_unit || requestPayload.unitNumber);

          const addressData = {
            address: fullAddress, // Full formatted address
            addressIncludesUnit: hasUnit, // Boolean indicating if address includes unit
            street_no: enrichmentResult.payload.street_no || requestPayload.street_no,
            street_name: enrichmentResult.payload.route_long || requestPayload.route_long,
            city: enrichmentResult.payload.city || requestPayload.city,
            state: enrichmentResult.payload.state_code || requestPayload.state_code,
            closing_state: enrichmentResult.payload.state_code || requestPayload.state_code, // Also set closing_state for SetCountyModal
            zip: enrichmentResult.payload.zip || requestPayload.zip,
            closing_county: enrichmentResult.payload.closing_county || requestPayload.closing_county,
            unitNumber: requestPayload.site_unit || requestPayload.unitNumber || "",
            formattedAddress: fullAddress,
          };
    //      console.log("Setting address data with unit number:", addressData.unitNumber, "address:", addressData.address, "addressIncludesUnit:", addressData.addressIncludesUnit);
          tabState.setAddressData(addressData);
        } else {
          // For cases where enrichment succeeds but payload is missing, still set address data
          const fullAddress = requestPayload.street_no && requestPayload.route_long ?
            `${requestPayload.street_no} ${requestPayload.route_long}, ${requestPayload.city}, ${requestPayload.state_code} ${requestPayload.zip}` : "";
          const hasUnit = !!(requestPayload.site_unit || requestPayload.unitNumber);

          const addressData = {
            address: fullAddress, // Full formatted address
            addressIncludesUnit: hasUnit, // Boolean indicating if address includes unit
            street_no: requestPayload.street_no,
            street_name: requestPayload.route_long,
            city: requestPayload.city,
            state: requestPayload.state_code,
            closing_state: requestPayload.state_code,
            zip: requestPayload.zip,
            closing_county: requestPayload.closing_county,
            unitNumber: requestPayload.site_unit || requestPayload.unitNumber || "",
            formattedAddress: fullAddress,
          };
       //   console.log("Setting address data (no payload) with unit number:", addressData.unitNumber, "address:", addressData.address, "addressIncludesUnit:", addressData.addressIncludesUnit);
          tabState.setAddressData(addressData);
        }
      }
    },
    [applyManualAddressEnrichment, tabState]
  );


  // ============================================================================
  // ENRICHMENT STATE MANAGEMENT
  // ============================================================================

  // Wrapper for selectSuggestion to trigger editManually section
  const handleSelectSuggestion = useCallback((suggestion) => {
    selectSuggestion(suggestion);
  }, [selectSuggestion]);

  // Watch for enrichment completion to show address form
  useEffect(() => {
    if (!isLoadingEnrichment && tabState.selectedAddressData && !tabState.zipValidationFailed) {
      tabState.showAddressForm();
    }
  }, [isLoadingEnrichment, tabState]);

  // On tab click: clear property-not-found block so it hides when switching tabs
  const handleTabClick = useCallback((tabName) => {
    clearPropertyNotFound();
    tabState.setActiveTab(tabName);
  }, [clearPropertyNotFound, tabState]);

  // Handle parcel selection with property enrichment
  const handleParcelSelected = useCallback(async (parcelData) => {
 //   console.log('📦 [AddressSearchPreview] Parcel selected:', parcelData);
  //  console.log('📦 [AddressSearchPreview] Parcel data fields:', Object.keys(parcelData));
    // Don't reset selection for parcel searches to avoid clearing the form
  //  console.log('🔄 [AddressSearchPreview] Starting parcel enrichment without full reset');
    // Build payload for property enrichment API
    const payload = {
      street_no: parcelData.street_no?.trim() || "",
      route_long: parcelData.street_name?.trim() || "",
      site_unit: parcelData.unitNumber?.trim() || "",
      city: parcelData.city?.trim() || "",
      state_code: parcelData.state?.trim() || "",
      state_name: parcelData.state?.trim() || "",
      unitNumber: parcelData.unitNumber?.trim() || "",
      closing_county: parcelData.closingCounty?.trim() || "",
      zip: parcelData.zip?.trim() || "",
    };

    try {
      // Use the common API method for property enrichment
      const result = await fetchCityStateForZip(payload, { mode });

      if (result.success) {
        // Enrichment successful - update state and call enrichment callback
        applyManualAddressEnrichment(result, payload, { fromParcelSelection: true });
        // Merge parcel data with closing_county from API response (only if API provides it)
        const updatedParcelData = {
          ...parcelData,
        };
        if (result.payload?.closing_county && result.payload.closing_county.trim()) {
      //    console.log('🔄 [AddressSearchPreview] Updating closing_county from API:', result.payload.closing_county);
          updatedParcelData.closing_county = result.payload.closing_county;
        }
        // Ensure address and addressIncludesUnit are preserved/updated
        const hasUnit = !!(parcelData.unitNumber && parcelData.unitNumber.trim());
        updatedParcelData.address = parcelData.formattedAddress || parcelData.address || "";
        updatedParcelData.addressIncludesUnit = hasUnit;

     //   console.log('📝 [AddressSearchPreview] Setting parcel address data:', updatedParcelData);
    //    console.log('🎯 [AddressSearchPreview] address:', updatedParcelData.address, 'addressIncludesUnit:', updatedParcelData.addressIncludesUnit);
        tabState.setAddressData(updatedParcelData);
        tabState.showAddressForm(); // Show the address form after setting data
        tabState.markParcelDataProcessed();
      } else {
        // Check if it's a "fail" response but address data is valid
        const isAddressValidButNoPropertyDetails =
          result.raw?.status === "fail" && result.raw?.data?.city;

        if (isAddressValidButNoPropertyDetails) {
          // Show property section with empty details
          applyManualAddressEnrichment(result, payload, { fromParcelSelection: true });
          // Include closing_county even if enrichment failed but address is valid
          const updatedParcelData = {
            ...parcelData,
          };
          if (result.payload?.closing_county && result.payload.closing_county.trim()) {
            updatedParcelData.closing_county = result.payload.closing_county;
          }
          // Ensure address and addressIncludesUnit are preserved
          const hasUnit = !!(parcelData.unitNumber && parcelData.unitNumber.trim());
          updatedParcelData.address = parcelData.formattedAddress || parcelData.address || "";
          updatedParcelData.addressIncludesUnit = hasUnit;

       //   console.log('📝 [AddressSearchPreview] Setting parcel address data (partial success):', updatedParcelData);
          tabState.setAddressData(updatedParcelData);
          tabState.showAddressForm(); // Show the address form after setting data
          tabState.markParcelDataProcessed();
        } else {
          // Complete failure - show property not found
          handleZipValidationFailure();
        }
      }
    } catch (error) {
      // API error - show property not found
      console.error("Parcel enrichment error:", error);
      handleZipValidationFailure();
    }
  }, [applyManualAddressEnrichment, handleZipValidationFailure, tabState, mode]);

  // Callback to update search query with formatted address (without triggering autocomplete)
  const updateSearchQuery = useCallback((formattedAddress) => {
    if (patch && suppressPredictions && formattedAddress) {
      // Suppress predictions before updating query to prevent autocomplete
      suppressPredictions();
      patch({
        query: formattedAddress,
        activeIndex: -1,
        showPreview: false,
        propertyNotFound: false,
      });
    }
  }, [patch, suppressPredictions]);

  // Search handlers hook (moved here after handleParcelSelected is declared)
  const {
    parcelState,
    ownerState,
    addressState,
    handleSearchParcel,
    handleSearchOwner,
    handleAddressSearch,
    updateParcelNumber,
    updateOwnerField,
  } = useSearchHandlers(openModal, closeModal, tabState.switchToAddressTab, handleParcelSelected, handleManualAddressResult, updateSearchQuery, mode);

  return (
    <div className={className || "seller-search-inner"}>
      <div className="seller-search">
        <div className="relative" style={{ width: '100%' }}>

          <div className="opensection-a" style={{ alignItems: 'flex-start' }}>
            {/* <div className="text-center" style={{ paddingTop: '20px' }}>
              <h2>Property Search</h2>
              <p>Please provide us with the address of the property, you would like to have an estimate for.</p>
            </div>*/}
            {/* Tab Navigation */}
            <div className="settings-tabs-wrapper">
              <ul className="settings-tabs">
                <li className={`tab-item ${tabState.activeTab === "Address" ? "active" : ""}`}
                  onClick={() => handleTabClick("Address")} >Address</li>
                <li className={`tab-item ${tabState.activeTab === "Owner" ? "active" : ""}`}
                  onClick={() => handleTabClick("Owner")} >Owner</li>
                <li className={`tab-item ${tabState.activeTab === "Parcel" ? "active" : ""}`}
                  onClick={() => handleTabClick("Parcel")} >Parcel</li>
              </ul>
            </div>
          </div>

          <div className="tbl-type-a">

            {/* Address Tab */}
            {tabState.activeTab === "Address" && (

              <AddressTab
                control={control}
                setValue={setValue}
                getValues={getValues}
                selectedAddressData={tabState.selectedAddressData}
                query={query}
                onChange={onChange}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                suggestions={suggestions}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                selectSuggestion={handleSelectSuggestion}
                suggestionItemRefs={suggestionItemRefs}
                showSiteUnit={showSiteUnit}
                siteUnit={siteUnit}
                handleSiteUnitCheckboxChange={handleSiteUnitCheckboxChange}
                handleSiteUnitInputChange={handleSiteUnitInputChange}
                handleAddressSearch={handleAddressSearch}
                addressState={addressState}
                openModal={openModal}
                closeModal={closeModal}
                suggestionSelected={tabState.suggestionSelected}
                setSuggestionSelected={tabState.setSuggestionSelected}
                mode={mode}
              />
            )}

            {/* Owner Tab */}
            {tabState.activeTab === "Owner" && (
              <OwnerTab
                control={control}
                ownerCountyAutocomplete={ownerCountyAutocomplete}
                ownerState={ownerState}
                updateOwnerField={updateOwnerField}
                handleSearchOwner={handleSearchOwner}
              />
            )}

            {/* Parcel Tab */}
            {tabState.activeTab === "Parcel" && (
              <ParcelTab
                parcelCountyAutocomplete={parcelCountyAutocomplete}
                parcelState={parcelState}
                updateParcelNumber={updateParcelNumber}
                handleSearchParcel={handleSearchParcel}
              />
            )}

          </div>

          {/* Property Preview */}
          <PropertyPreview
            isLoadingEnrichment={tabState.zipValidationFailed ? false : (tabState.parcelDataProcessed ? false : isLoadingEnrichment)}
            propertyNotFound={tabState.zipValidationFailed ? true : propertyNotFound}
            showPreview={tabState.zipValidationFailed ? false : (tabState.parcelDataProcessed ? true : showPreview)}
            header={header}
            ttbData={tabState.zipValidationFailed ? {} : (ttbData || {})}
            panoramaContainerRef={panoramaContainerRef}
            satelliteContainerRef={satelliteContainerRef}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
          />

        </div>
      </div>
    </div>
  );
});

AddressSearchPreview.displayName = "AddressSearchPreview";

export default AddressSearchPreview;